// @catalog F.010
/**
 * ForecastTable — tabla densa con 11 columnas (handoff Fase 4.a).
 *
 * Spec literal:
 *   - README §2 Table (líneas 119-144) — 11 columnas + sticky headers + sort.
 *   - prototype/forecast-page.jsx líneas 308-364 — SortHeader + headers + tbody.
 *   - prototype/styles.css líneas 311-379 — .fc-tbl* + .cb-cell + sticky + urgency.
 *
 * Columnas (11):
 *   1. Checkbox (32px)
 *   2. Producto (min 260px) — sortable=unifiedName
 *   3. Inv/Reorden (right) — sortable=totalAvailableQty
 *   4. Ventas 4m (right) — sortable=recentSalesAmount
 *   5. DCI
 *   6. ABC·XYZ — sortable=abcXyzSegment
 *   7. Demanda — sortable=demandClassification
 *   8. Método — sortable=matchingMethod
 *   9. Pronóstico/mes (right) — sortable=forecastNextPeriod (demanda mensual, NO la compra «Reposición» del diálogo)
 *   10. Conf. (right) — sortable=matchingConfidence
 *   11. Estado
 *
 * Headers sticky position con sort indicators ▲▼.
 * Body usa RowComponent (prop) que por default es un stub minimal — feature 6
 * crea la implementación completa en components/Forecast/ForecastProductRow.tsx
 * y feature 11 (orchestrator) la cablea aquí vía RowComponent.
 *
 * Constraints:
 *   - <500 LoC (P7).
 *   - Native <table>/<thead>/<tbody>/<tr>/<td>/<th> para position:sticky + ::before urgency.
 *   - NO <Grid> MUI; envoltorios MUI Box/Stack permitidos.
 */
import { Box } from "@mui/material";
import type { ComponentType, Ref } from "react";
import type { ProductData, SortField, SortOrder } from "./types";

export type RowOpenAction = (
  e: React.MouseEvent<HTMLElement>,
  productId: string,
  displayName: string,
) => void;

export interface ForecastTableRowProps {
  product: ProductData;
  displayName: string;
  selected: boolean;
  onToggleRow: (id: string) => void;
  onOpenInventoryPopover: RowOpenAction;
  onOpenSalesPopover: RowOpenAction;
  onOpenDetail: (id: string, displayName: string) => void;
  onToggleActiveForPurchase: (id: string) => void;
}

export interface ForecastTableProps {
  xScrollRef?: Ref<HTMLDivElement | null>;
  hContentRef?: Ref<HTMLDivElement | null>;
  paginatedProducts: ProductData[];
  filteredAndSortedProductsLength: number;
  selectedIds: Set<string>;
  isSelected: (id: string) => boolean;
  toggleRow: (id: string) => void;
  toggleHeaderSelection: () => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  onOpenInventoryPopover: RowOpenAction;
  onOpenSalesPopover: RowOpenAction;
  onOpenDetail: (id: string, displayName: string) => void;
  onToggleActiveForPurchase: (id: string) => void;
  getDisplayText: (
    name: string | { en_US: string; es_MX: string },
  ) => string;
  RowComponent?: ComponentType<ForecastTableRowProps>;
}

interface SortHeaderProps {
  field: SortField;
  label: React.ReactNode;
  align?: "left" | "right";
  /** Tooltip nativo (atributo title) para desambiguar la columna. */
  title?: string;
  currentField: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
}

function SortHeader(props: SortHeaderProps): React.ReactElement {
  const { field, label, align = "left", title, currentField, currentOrder, onSort } = props;
  const sorted = currentField === field;
  return (
    <Box
      component="th"
      title={title}
      onClick={() => onSort(field)}
      tabIndex={0}
      onKeyDown={(e: React.KeyboardEvent<HTMLTableCellElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSort(field);
        }
      }}
      aria-sort={sorted ? (currentOrder === "asc" ? "ascending" : "descending") : "none"}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        bgcolor: "var(--bg)",
        borderBottom: "1px solid var(--border)",
        padding: "8px 10px",
        textAlign: align,
        fontWeight: 500,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: sorted ? "var(--ink-1)" : "var(--ink-3)",
        whiteSpace: "nowrap",
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { color: "var(--ink-1)" },
      }}
    >
      {label}
      <Box
        component="span"
        sx={{
          ml: "3px",
          fontSize: 9,
          opacity: sorted ? 1 : 0.4,
          color: sorted ? "var(--ink-1)" : "inherit",
        }}
      >
        {sorted ? (currentOrder === "asc" ? "▲" : "▼") : "▲▼"}
      </Box>
    </Box>
  );
}

function PlainHeader({
  children,
  align = "left",
  width,
}: {
  children?: React.ReactNode;
  align?: "left" | "right";
  width?: number | string;
}): React.ReactElement {
  return (
    <Box
      component="th"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        bgcolor: "var(--bg)",
        borderBottom: "1px solid var(--border)",
        padding: "8px 10px",
        textAlign: align,
        fontWeight: 500,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "var(--ink-3)",
        whiteSpace: "nowrap",
        width,
      }}
    >
      {children}
    </Box>
  );
}

function HeaderCheckbox({
  selectedCount,
  visibleCount,
  onToggle,
}: {
  selectedCount: number;
  visibleCount: number;
  onToggle: () => void;
}): React.ReactElement {
  const allSelected = visibleCount > 0 && selectedCount >= visibleCount;
  return (
    <Box
      component="th"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 2,
        width: 32,
        paddingLeft: "10px",
        paddingRight: "6px",
        bgcolor: "var(--bg)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <Box
        role="button"
        aria-label="Seleccionar todos visibles"
        aria-pressed={allSelected}
        onClick={onToggle}
        data-testid="forecast-table-header-checkbox"
        sx={{
          width: 15,
          height: 15,
          border: "1.5px solid var(--border-strong)",
          borderRadius: "3px",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
          bgcolor: allSelected ? "var(--accent)" : "var(--surface)",
          borderColor: allSelected ? "var(--accent)" : "var(--border-strong)",
          color: "#fff",
          "&:hover": { borderColor: "var(--accent)" },
        }}
      >
        {allSelected ? "✓" : ""}
      </Box>
    </Box>
  );
}

/**
 * RowStub — placeholder mínimo de 11 td cells. Feature 6 entrega la row completa
 * con barras/sparklines/chips/etc.; feature 11 (orchestrator) la cablea vía
 * RowComponent prop.
 */
function RowStub(props: ForecastTableRowProps): React.ReactElement {
  const { product, displayName, selected, onToggleRow, onOpenDetail } = props;
  const inactive = product.activeForPurchase === false;
  return (
    <Box
      component="tr"
      className={selected ? "selected" : undefined}
      sx={{
        position: "relative",
        opacity: inactive ? 0.55 : 1,
        bgcolor: selected ? "var(--accent-soft)" : "transparent",
        "&:hover": { bgcolor: selected ? "var(--accent-soft)" : "var(--bg)" },
        "& td": {
          padding: "9px 10px",
          borderBottom: "1px solid var(--border)",
          verticalAlign: "middle",
          fontVariantNumeric: "tabular-nums",
        },
      }}
    >
      <td onClick={() => !inactive && onToggleRow(product.unifiedProductId)} style={{ cursor: inactive ? "not-allowed" : "pointer", width: 32 }}>
        <Box
          sx={{
            width: 15,
            height: 15,
            border: "1.5px solid var(--border-strong)",
            borderRadius: "3px",
            display: "grid",
            placeItems: "center",
            bgcolor: selected ? "var(--accent)" : "var(--surface)",
            borderColor: selected ? "var(--accent)" : "var(--border-strong)",
            color: "#fff",
            opacity: inactive ? 0.3 : 1,
            fontSize: 10,
          }}
        >
          {selected ? "✓" : ""}
        </Box>
      </td>
      <td>
        <Box
          component="span"
          onClick={() => onOpenDetail(product.unifiedProductId, displayName)}
          sx={{ cursor: "pointer", color: "var(--ink-1)", fontWeight: 500, "&:hover": { color: "var(--accent)", textDecoration: "underline" } }}
        >
          {displayName}
        </Box>
      </td>
      <td style={{ textAlign: "right" }}>{product.totalAvailableQty ?? "—"} / {product.reorderPoint ?? "—"}</td>
      <td style={{ textAlign: "right" }}>{product.recentSalesAmount ?? "—"}</td>
      <td>{product.dciStoreCount ?? "—"}/{product.dciTargetStores ?? "—"}</td>
      <td>{product.abcXyzSegment ?? "—"}</td>
      <td>{product.demandClassification ?? "—"}</td>
      <td>{product.matchingMethod}</td>
      <td style={{ textAlign: "right" }}>{product.forecastNextPeriod ?? "—"}</td>
      <td style={{ textAlign: "right" }}>{Math.round((product.matchingConfidence ?? 0) * 100)}%</td>
      <td>{product.activeForPurchase === false ? "OFF" : "ON"}</td>
    </Box>
  );
}

export function ForecastTable(props: ForecastTableProps): React.ReactElement {
  const {
    xScrollRef,
    hContentRef,
    paginatedProducts,
    filteredAndSortedProductsLength,
    selectedIds,
    isSelected,
    toggleRow,
    toggleHeaderSelection,
    sortField,
    sortOrder,
    onSort,
    onOpenInventoryPopover,
    onOpenSalesPopover,
    onOpenDetail,
    onToggleActiveForPurchase,
    getDisplayText,
    RowComponent = RowStub,
  } = props;

  const visibleAll = paginatedProducts.length;

  return (
    <Box
      ref={xScrollRef}
      data-testid="forecast-table-xscroll"
      sx={{
        gridRow: "2 / 3",
        overflowX: "auto",
        overflowY: "auto",
        bgcolor: "var(--surface)",
      }}
    >
      <Box
        ref={hContentRef}
        sx={{ display: "inline-block", minWidth: "100%", width: "max-content" }}
      >
        <Box
          component="table"
          data-testid="forecast-table"
          sx={{
            width: "100%",
            borderCollapse: "separate",
            borderSpacing: 0,
            fontSize: 13,
            "& tbody tr": { position: "relative" },
            '& tbody tr[data-urgency="critical"] td:first-of-type::before': {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: "var(--neg)",
            },
            '& tbody tr[data-urgency="warn"] td:first-of-type::before': {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: "3px",
              background: "var(--warn)",
            },
          }}
        >
          <thead>
            <tr>
              <HeaderCheckbox
                selectedCount={selectedIds.size}
                visibleCount={Math.max(filteredAndSortedProductsLength, visibleAll)}
                onToggle={toggleHeaderSelection}
              />
              <SortHeader field="unifiedName" label="Producto" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortHeader field="totalAvailableQty" label="Inv / Reorden" align="right" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortHeader field="recentSalesAmount" label="Ventas 4m" align="right" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <PlainHeader>DCI</PlainHeader>
              <SortHeader field="abcXyzSegment" label="ABC·XYZ" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortHeader field="demandClassification" label="Demanda" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortHeader field="matchingMethod" label="Método" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortHeader field="forecastNextPeriod" label="Pronóstico/mes" align="right" title="Demanda mensual pronosticada (u/mes). NO es la compra: la cantidad a comprar aparece como «Reposición» en el diálogo, calculada desde este pronóstico + stock + horizonte." currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortHeader field="matchingConfidence" label="Conf." align="right" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <PlainHeader>Estado</PlainHeader>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map((product) => {
              const displayName = getDisplayText(product.unifiedName);
              return (
                <RowComponent
                  key={product.unifiedProductId}
                  product={product}
                  displayName={displayName}
                  selected={isSelected(product.unifiedProductId)}
                  onToggleRow={toggleRow}
                  onOpenInventoryPopover={onOpenInventoryPopover}
                  onOpenSalesPopover={onOpenSalesPopover}
                  onOpenDetail={onOpenDetail}
                  onToggleActiveForPurchase={onToggleActiveForPurchase}
                />
              );
            })}
          </tbody>
        </Box>
      </Box>
    </Box>
  );
}
